import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import type { MutationResponse } from '@/types/api/mutations';

import { type AppColumnDef, createAppColumnHelper } from '@/components';
import { SortableHeader } from '@/components/DataTable';
import { TableActions } from '@/components/TableActions';

import { DisplayToast, notifyMutationError, toMoneyFormat } from '@/helpers';

import {
  DISPLAY_DATES_FORMAT,
  DISPLAY_DATETIME_FORMAT,
} from '@/constants/dates';

import { HttpClient } from '@/network/HttpClient';

// --- Types ---

type FormatType = 'money' | 'boolean' | 'date' | 'datetime';

type ColumnOptions<TItem> = {
  /** Built-in cell formatter: `'money'` (toMoneyFormat), `'boolean'` (+/-), `'date'`, `'datetime'` */
  format?: FormatType;
  /** Wraps cell value in `<span className="font-medium">` */
  bold?: boolean;
  /** Custom render function. Overrides `format` and `bold` when provided */
  render?: (row: TItem) => ReactNode;
};

type DeleteConfig<TItem> = {
  /** API endpoint for DELETE request (`${url}/${id}`) */
  url: string;
  /** Query key to invalidate after successful deletion. A string is the legacy
   * `_QUERY_KEY` constant; an array accepts a key factory (`<entity>Keys.all()`). */
  queryKey: string | QueryKey;
  /** Entity name for confirmation dialog. Defaults to `row.name` */
  entityName?: (row: TItem) => string;
  /** Custom success handler. Overrides built-in toast + invalidate */
  onSuccess?: (response: unknown) => void;
  /** Custom error handler. Overrides built-in error toast */
  onError?: (error: unknown) => void;
};

type ActionsConfig<TItem> = {
  /** Returns edit page URL for the row */
  edit?: (row: TItem) => string;
  /** Delete mutation config with confirmation dialog */
  delete?: DeleteConfig<TItem>;
};

type ColConfig<TItem> = {
  _type: 'col';
  accessor: keyof TItem & string;
  header: string;
  options?: ColumnOptions<TItem>;
};

type SortableConfig<TItem> = {
  _type: 'sortable';
  accessor: keyof TItem & string;
  header: string;
  options?: ColumnOptions<TItem>;
};

type DisplayConfig<TItem> = {
  _type: 'display';
  id: string;
  header: string;
  render: (row: TItem) => ReactNode;
};

type ActionsColumnConfig<TItem> = {
  _type: 'actions';
  config: ActionsConfig<TItem>;
};

type ColumnConfig<TItem> =
  | ColConfig<TItem>
  | SortableConfig<TItem>
  | DisplayConfig<TItem>
  | ActionsColumnConfig<TItem>;

type ColumnHelpers<TItem> = {
  /** Regular column bound to a field of `TItem` */
  col: (
    /** Field name from `TItem` (type-safe) */
    accessor: keyof TItem & string,
    /** Column header text */
    header: string,
    /** Format, bold, or custom render */
    options?: ColumnOptions<TItem>,
  ) => ColumnConfig<TItem>;
  /** Sortable column — same as `col` but with `SortableHeader` and sorting enabled */
  sortable: (
    /** Field name from `TItem` (type-safe) */
    accessor: keyof TItem & string,
    /** Column header text */
    header: string,
    /** Format, bold, or custom render */
    options?: ColumnOptions<TItem>,
  ) => ColumnConfig<TItem>;
  /** Computed column without accessor — full custom render */
  display: (
    /** Unique column id */
    id: string,
    /** Column header text */
    header: string,
    /** Render function receiving the full row */
    render: (row: TItem) => ReactNode,
  ) => ColumnConfig<TItem>;
  /** Edit/delete actions column with built-in TableActions wiring */
  actions: (config: ActionsConfig<TItem>) => ColumnConfig<TItem>;
};

// --- Cell Renderer Helper ---

const renderCellValue = <TItem,>(
  value: unknown,
  options?: ColumnOptions<TItem>,
  row?: TItem,
): ReactNode => {
  if (options?.render && row) {
    return options.render(row);
  }

  switch (options?.format) {
    case 'money':
      return value != null ? toMoneyFormat(value as number) : '';
    case 'boolean':
      return value ? '+' : '-';
    case 'date':
      return value != null
        ? format(parseISO(String(value)), DISPLAY_DATES_FORMAT)
        : '';
    case 'datetime':
      return value != null
        ? format(parseISO(String(value)), DISPLAY_DATETIME_FORMAT)
        : '';
  }

  const displayValue = value != null ? String(value) : '';

  if (options?.bold) {
    return <span className="font-bold">{displayValue}</span>;
  }

  return displayValue;
};

// --- Mutation Response Handlers ---

const notifyAndInvalidate = (
  response: unknown,
  queryKey: string | QueryKey,
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  const resp = response as MutationResponse | undefined;

  if (resp?.flash_type === 'error') {
    DisplayToast.error(resp.flash_message);
    return;
  }

  if (resp?.flash_message) {
    DisplayToast.success(resp.flash_message);
  }

  queryClient.invalidateQueries({
    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
  });
};

type MutationCallbacksConfig = {
  queryKey: string | QueryKey;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown) => void;
};

const createMutationCallbacks = (
  config: MutationCallbacksConfig,
  queryClient: ReturnType<typeof useQueryClient>,
) => ({
  onSuccess: (response: unknown) => {
    if (config.onSuccess) {
      config.onSuccess(response);
      return;
    }
    notifyAndInvalidate(response, config.queryKey, queryClient);
  },
  onError: (error: unknown) => {
    if (config.onError) {
      config.onError(error);
      return;
    }
    notifyMutationError(error);
  },
});

// --- Hook ---

/**
 * Declarative column builder for DataTable.
 *
 * Provides a concise builder API for defining table columns. Supports built-in formatters,
 * sortable headers, custom renderers, and an `actions` helper that wires edit links and
 * delete mutations (with confirmation dialog and error handling) automatically.
 *
 * ## Helpers
 *
 * - **`col(accessor, header, options?)`** — regular column bound to a field of `TItem`
 * - **`sortable(accessor, header, options?)`** — same as `col` but with `SortableHeader` and sorting enabled
 * - **`display(id, header, render)`** — computed column without accessor, full custom render via `(row: TItem) => ReactNode`
 * - **`actions(config)`** — edit/delete actions column with built-in `TableActions` wiring
 *
 * ## Column options
 *
 * - `format` — built-in cell formatter: `'money'` | `'boolean'` | `'date'` | `'datetime'`
 * - `bold` — wraps value in `<span className="font-medium">`
 * - `render` — custom render function `(row: TItem) => ReactNode`, overrides `format` and `bold`
 *
 * ## Actions config
 *
 * - `edit` — `(row: TItem) => string` returning the edit URL
 * - `delete.url` — API endpoint for DELETE request
 * - `delete.queryKey` — query key to invalidate after successful deletion.
 *   Legacy `_QUERY_KEY` string, or an array from a key factory (`<entity>Keys.all()`)
 * - `delete.entityName` — optional `(row: TItem) => string` for confirmation dialog (defaults to `row.name`)
 * - `delete.onSuccess` / `delete.onError` — optional custom handlers (override built-in toast + invalidate)
 *
 * @example
 * ```tsx
 * const columns = useTableColumns<ServiceListItem>(({ col, sortable, actions }) => [
 *   sortable('name', t('Name'), { bold: true }),
 *   col('price', t('Price'), { format: 'money' }),
 *   col('active', t('Active'), { format: 'boolean' }),
 *   col('locations', t('Locations'), {
 *     render: (row) => row.locations.map((l) => l.name).join(', '),
 *   }),
 *   actions({
 *     edit: (row) => `${url}/${row.id}/edit`,
 *     delete: { url: fetch_url, queryKey: QUERY_KEY },
 *   }),
 * ])
 * ```
 */
export const useTableColumns = <TItem extends { id: number | string }>(
  builder: (helpers: ColumnHelpers<TItem>) => ColumnConfig<TItem>[],
): AppColumnDef<TItem>[] => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteEntity } = useMutation({
    mutationFn: (params: { url: string; id: number | string }) =>
      HttpClient.request<MutationResponse>({
        method: 'DELETE',
        url: `${params.url}/${params.id}`,
      }),
  });

  return useMemo(() => {
    const columnHelper = createAppColumnHelper<TItem>();

    const helpers: ColumnHelpers<TItem> = {
      col: (accessor, header, options) => ({
        _type: 'col' as const,
        accessor,
        header,
        options,
      }),
      sortable: (accessor, header, options) => ({
        _type: 'sortable' as const,
        accessor,
        header,
        options,
      }),
      display: (id, header, render) => ({
        _type: 'display' as const,
        id,
        header,
        render,
      }),
      actions: (config) => ({ _type: 'actions' as const, config }),
    };

    const configs = builder(helpers);

    return configs.map((config): AppColumnDef<TItem> => {
      switch (config._type) {
        case 'col':
          return columnHelper.accessor(() => config.accessor, {
            id: config.accessor,
            header: config.header,
            cell: ({ row }) =>
              renderCellValue(
                row.original[config.accessor],
                config.options,
                row.original,
              ),
          }) as AppColumnDef<TItem>;

        case 'sortable':
          return columnHelper.accessor(() => config.accessor, {
            id: config.accessor,
            header: ({ column }) => (
              <SortableHeader column={column}>{config.header}</SortableHeader>
            ),
            cell: ({ row }) =>
              renderCellValue(
                row.original[config.accessor],
                config.options,
                row.original,
              ),
            enableSorting: true,
          }) as AppColumnDef<TItem>;

        case 'display':
          return columnHelper.display({
            id: config.id,
            header: config.header,
            cell: ({ row }) => config.render(row.original),
          }) as AppColumnDef<TItem>;

        case 'actions': {
          const actionsConfig = config.config;

          const buildEditProp = (item: TItem) =>
            actionsConfig.edit
              ? { edit: { url: actionsConfig.edit(item) } }
              : {};

          const buildRemoveProp = (item: TItem) => {
            const deleteConfig = actionsConfig.delete;
            if (!deleteConfig) return {};
            return {
              remove: {
                entityName: deleteConfig.entityName
                  ? deleteConfig.entityName(item)
                  : (item as unknown as { name?: string }).name,
                id: item.id,
                onConfirm: () => {
                  deleteEntity(
                    { url: deleteConfig.url, id: item.id },
                    createMutationCallbacks(deleteConfig, queryClient),
                  );
                },
              },
            };
          };

          return columnHelper.display({
            id: 'actions',
            header: t('Actions'),
            cell: ({ row }) => (
              <TableActions
                {...buildEditProp(row.original)}
                {...buildRemoveProp(row.original)}
              />
            ),
          }) as AppColumnDef<TItem>;
        }
      }
    });
  }, [builder, deleteEntity, queryClient, t]);
};
